/**
 * PendingRegistrations Component
 * Admin interface for managing registration requests
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatServerDateTime } from '../utils/dateFormat';
import './PendingRegistrations.css';

function PendingRegistrations() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'all' 
        ? `${process.env.REACT_APP_API_URL}/api/admin/registration/requests`
        : `${process.env.REACT_APP_API_URL}/api/admin/registration/requests?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      setMessage({ type: 'error', text: 'Failed to load registration requests' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setModalAction('approve');
    setShowModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setModalAction('reject');
    setRejectReason('');
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    setActionLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const endpoint = modalAction === 'approve' ? 'approve' : 'reject';
      const url = `${process.env.REACT_APP_API_URL}/api/admin/registration/requests/${selectedRequest.id}/${endpoint}`;

      const data = modalAction === 'reject' ? { reason: rejectReason } : {};

      const response = await axios.post(url, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (modalAction === 'approve' && response.data.emailSent === false) {
          setMessage({
            type: 'error',
            text: `User approved, but the credentials email to ${selectedRequest.email} FAILED to send. ` +
              `Give them these directly — Username: ${response.data.user.username}, ` +
              `Temporary Password: ${response.data.temporaryPassword} (not shown again)`
          });
        } else {
          setMessage({
            type: 'success',
            text: modalAction === 'approve'
              ? `User approved! Credentials sent to ${selectedRequest.email}`
              : 'Request rejected'
          });
        }
        loadRequests();
        setShowModal(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || `Failed to ${modalAction} request` 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return formatServerDateTime(dateString);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="badge badge-warning">Pending</span>,
      approved: <span className="badge badge-success">Approved</span>,
      rejected: <span className="badge badge-danger">Rejected</span>,
      'auto-approved': <span className="badge badge-info">Auto-Approved</span>
    };
    return badges[status] || status;
  };

  if (loading) {
    return <div className="loading">Loading registration requests...</div>;
  }

  return (
    <div className="pending-registrations">
      <div className="registrations-header">
        <h2>Registration Requests</h2>
        <div className="filter-buttons">
          <button 
            className={filter === 'pending' ? 'active' : ''} 
            onClick={() => setFilter('pending')}
          >
            Pending ({requests.filter(r => r.status === 'pending').length})
          </button>
          <button 
            className={filter === 'approved' ? 'active' : ''} 
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button 
            className={filter === 'rejected' ? 'active' : ''} 
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="no-requests">
          <p>No {filter !== 'all' ? filter : ''} registration requests found.</p>
        </div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Job Title</th>
                <th>Role</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.id}>
                  <td>{request.full_name}</td>
                  <td>{request.email}</td>
                  <td>{request.job_title || '-'}</td>
                  <td>{request.profession_role === 'nurse' ? 'Nurse' : 'Sonographer'}</td>
                  <td>{formatDate(request.created_at)}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    {request.status === 'pending' ? (
                      <div className="action-buttons">
                        <button 
                          className="btn-approve" 
                          onClick={() => handleApprove(request)}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn-reject" 
                          onClick={() => handleReject(request)}
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="no-action">
                        {request.reviewed_at ? formatDate(request.reviewed_at) : '-'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowModal(false)}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {modalAction === 'approve' ? 'Approve Registration' : 'Reject Registration'}
            </h3>
            
            <div className="request-details">
              <p><strong>Name:</strong> {selectedRequest.full_name}</p>
              <p><strong>Email:</strong> {selectedRequest.email}</p>
              <p><strong>Job Title:</strong> {selectedRequest.job_title || 'Not provided'}</p>
              <p><strong>Role:</strong> {selectedRequest.profession_role === 'nurse' ? 'Nurse' : 'Sonographer'}</p>
              {selectedRequest.message && (
                <p><strong>Message:</strong> {selectedRequest.message}</p>
              )}
            </div>

            {modalAction === 'approve' ? (
              <div className="approve-info">
                <p>This will:</p>
                <ul>
                  <li>Create a user account</li>
                  <li>Generate a temporary password</li>
                  <li>Send login credentials to {selectedRequest.email}</li>
                </ul>
              </div>
            ) : (
              <div className="reject-form">
                <label>Reason for rejection (optional):</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="This will be sent to the applicant..."
                  rows="4"
                />
              </div>
            )}

            <div className="modal-actions">
              <button 
                className={modalAction === 'approve' ? 'btn-approve' : 'btn-reject'}
                onClick={confirmAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : `Confirm ${modalAction === 'approve' ? 'Approval' : 'Rejection'}`}
              </button>
              <button 
                className="btn-cancel" 
                onClick={() => setShowModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingRegistrations;
